import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { retry, shareReplay, catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { RegistroCandidatoRequestInterface } from '../interfaces/candidato/registro-candidato-request.interface';
import { CandidatoLoginResponseInterface } from "../interfaces/candidato/candidato-login-response.interface";
import { AuthApiService } from 'src/app/core/services/auth-api-service';
import { EncryptionService } from './encryption.service';

@Injectable({
  providedIn: 'root'
})
export class EstudiosAcademicosApiService {
  user$ = this.authApiService.getUser();
  private tokenKey = 'authToken';
  private isAuthenticated = new BehaviorSubject<boolean>(this.hasToken());

  constructor(private http: HttpClient, private authApiService: AuthApiService, private encryptionService: EncryptionService) { }

  private hasToken(): boolean {
    return !!localStorage.getItem(this.tokenKey);
  }

  agregarEstudiosAcademicos(idNivel: number, idInstitucion: number, idCarrera: number, fechaInicio: string, fechaFin: string): Observable<any> {
    let _request: any = [{
      "uid": '',
      "ues_id_estudio": null,
      "ues_id_usuario": '',
      "ues_id_nivel_estudio": idNivel,
      "ues_id_carrera": idCarrera,
      "ues_id_pais": "MX",
      "ues_id_institucion_educativa": idInstitucion,
      "ues_anio_ini": this.obtenerMesYAnio(fechaInicio).anio,
      "ues_mes_ini": this.obtenerMesYAnio(fechaInicio).mes,
      "ues_anio_fin": this.obtenerMesYAnio(fechaInicio).anio,
      "ues_mes_fin": this.obtenerMesYAnio(fechaInicio).mes,
      "ues_actual": null,
      "ues_comprobante": 0,
      "ues_grado": null
    }];

    this.user$.subscribe(user => {
      _request[0].uid = user?.uid;
      _request[0].ues_id_usuario = user?.uid;
    });

    return this.http
      .post<RegistroCandidatoRequestInterface>(`${environment.urlApiCandidato}/user/agregaEstudios`, _request)
      .pipe(shareReplay(1))
  }

  getEstudiosAcademicos(): Observable<any> {
    let _uid: string = '';
    this.user$.subscribe(user => {
      _uid = user?.uid || '';
    });

    return this.http
      .get<any>(`${environment.urlApiCandidato}/user/obtenerEstudios?pais=MX&uid=${_uid}`)
      .pipe(shareReplay(1))
  }

  actualizarEstudios(idEstudio: number, idNivel: number, idInstitucion: number, idCarrera: number, fechaInicio: string, fechaFin: string): Observable<any> {
    let _request = [{
      "ues_id_estudio": idEstudio,
      "ues_id_usuario": '',
      "uid": '',
      "ues_id_nivel_estudio": idNivel,
      "ues_id_carrera": idCarrera,
      "ues_id_pais": "MX",
      "ues_id_institucion_educativa": idInstitucion,
      "ues_anio_ini": this.obtenerMesYAnio(fechaInicio).anio,
      "ues_mes_ini": this.obtenerMesYAnio(fechaInicio).mes,
      "ues_anio_fin": this.obtenerMesYAnio(fechaFin).anio,
      "ues_mes_fin": this.obtenerMesYAnio(fechaFin).mes,
      "ues_actual": null,
      "ues_comprobante": 0,
      "ues_grado": null
  }];

    this.user$.subscribe(user => {
      _request[0].ues_id_usuario = user?.uid || '';
      _request[0].uid = user?.uid || '';
    });

    return this.http
      .post<any>(`${environment.urlApiCandidato}/user/actualizaEstudios`, _request)
      .pipe(shareReplay(1))
  }

  obtenerMesYAnio(fecha: string): { mes: number, anio: number } {
    if (fecha === '') {
      return { mes: 0, anio: 0};
    }
    
    const [mesStr, anioStr] = fecha.split("-");
    const anio = parseInt(anioStr, 10);
    const mes = parseInt(mesStr, 10);

    if (isNaN(mes) || isNaN(anio)) {
      throw new Error("Formato de fecha inválido. Se espera 'MM-YYYY'");
    }

    return { mes, anio };
  }

  eliminarEstudios(idEstudio: string): Observable<any> {
    const _idEstudioEncrypt = this.encryptionService.encryptToBase64(idEstudio);

    let _request = {
      "idUsuario": "",
      "idEstudio": _idEstudioEncrypt
    }

    this.user$.subscribe(user => {
      _request.idUsuario = user?.uid || '';
    });

    return this.http
      .post<any>(`${environment.urlApiCandidato}/user/eliminaEstudios`, _request)
      .pipe(shareReplay(1))
  }

  getCursos(): Observable<any> {
    let _uid: string = '';
    this.user$.subscribe(user => {
      _uid = user?.uid || '';
    });

    return this.http
      .get<any>(`${environment.urlApiCandidato}/user/course/obtener-curso?uid=${_uid}`)
      .pipe(shareReplay(1))
  }

  agregarCurso(idTipoCurso: string, nombre: string, institucion: string, fechaInicio: string, fechaFin: string): Observable<any> {

    let _request: any = {
      "uid": "",
      "idTipoCurso": idTipoCurso,
      "nombre": nombre,
      "institucion": institucion,
      "anioIni": this.obtenerMesYAnio(fechaInicio).anio,
      "mesIni": this.obtenerMesYAnio(fechaInicio).mes,
      "anioFin": ((this.obtenerMesYAnio(fechaFin).anio===0)? '' : this.obtenerMesYAnio(fechaFin).anio),
      "mesFin": ((this.obtenerMesYAnio(fechaFin).mes===0)? '' : this.obtenerMesYAnio(fechaFin).mes)
    };

    this.user$.subscribe(user => {
      _request.uid = user?.uid;
    });

    return this.http
      .post<any>(`${environment.urlApiCandidato}/user/course/agregar-curso`, _request)
      .pipe(shareReplay(1))
  }

  actualizarCurso(idCurso: number, idTipoCurso: string, nombre: string, institucion: string, fechaInicio: string, fechaFin: string): Observable<any> {
    let _request: any = {
      "idCurso": idCurso,
      "uid": "",
      "idTipoCurso": idTipoCurso,
      "nombre": nombre,
      "institucion": institucion,
      "anioIni": this.obtenerMesYAnio(fechaInicio).anio,
      "mesIni": this.obtenerMesYAnio(fechaInicio).mes,
      "anioFin": ((this.obtenerMesYAnio(fechaFin).anio===0)? '' : this.obtenerMesYAnio(fechaFin).anio),
      "mesFin": ((this.obtenerMesYAnio(fechaFin).mes===0)? '' : this.obtenerMesYAnio(fechaFin).mes)
    };

    this.user$.subscribe(user => {
      _request.uid = user?.uid;
    });

    return this.http
      .post<any>(`${environment.urlApiCandidato}/user/course/actualizar-curso`, _request)
      .pipe(shareReplay(1))
  }

  eliminarCurso(id: string): Observable<any> {
    // const _idEstudioEncrypt = this.encryptionService.encryptToBase64(id);
    let uid: string = '';

    this.user$.subscribe(user => {
      uid = user?.uid || '';
    });

    return this.http
      .post<any>(`${environment.urlApiCandidato}/user/course/eliminar-curso?uid=${uid}&idCurso=${id}`,{})
      .pipe(shareReplay(1))
  }

  getCaralogoIdiomas(): Observable<any> {
    let _uid: string = '';
    this.user$.subscribe(user => {
      _uid = user?.uid || '';
    });

    return this.http
      .get<any>(`${environment.urlApiCandidato}/catalogo/idiomas`)
      .pipe(shareReplay(1))
  }

  getCatalogoNivelManejoIdiomas(): Observable<any> {
    let _uid: string = '';
    this.user$.subscribe(user => {
      _uid = user?.uid || '';
    });

    return this.http
      .get<any>(`${environment.urlApiCandidato}/catalogo/niveles-manejo`)
      .pipe(shareReplay(1))
  }

  getIdiomas(): Observable<any> {
    let _uid: string = '';
    this.user$.subscribe(user => {
      _uid = user?.uid || '';
    });

    return this.http
      .get<any>(`${environment.urlApiCandidato}/user/language/obtenerIdioma?uid=${_uid}`)
      .pipe(shareReplay(1))
  }

  agregarIdioma(idIdioma: number, idNivelManejo: number): Observable<any> {
    let _request: any = {
      "uid" : "",
      "idIdioma": idIdioma,
      "idNivelManejo": idNivelManejo
    };

    this.user$.subscribe(user => {
      _request.uid = user?.uid;
    });

    return this.http
      .post<any>(`${environment.urlApiCandidato}/user/language/agregaIdioma`, _request)
      .pipe(shareReplay(1))
  }

  actualizarIdioma(idIdioma: number, idNivelManejo: number): Observable<any> {
    let _request: any = {
      "uid" : "",
      "idIdioma": idIdioma,
      "idNivelManejo": idNivelManejo
    };

    this.user$.subscribe(user => {
      _request.uid = user?.uid;
    });

    return this.http
      .post<any>(`${environment.urlApiCandidato}/user/language/actualizaNivelIdioma`, _request)
      .pipe(shareReplay(1))
  }

  eliminarIdioma(id: string): Observable<any> {
    let uid: string = '';

    this.user$.subscribe(user => {
      uid = user?.uid || '';
    });

    return this.http
      .post<any>(`${environment.urlApiCandidato}/user/language/eliminaIdioma?uid=${uid}&idIdioma=${id}`, {})
      .pipe(shareReplay(1))
  }
}